import { config } from "../../config";

/**
 * The score/status pieces shared by the directory artwork card and the
 * directory-edit table.
 *
 * They lived twice: as JSX inside `DirectoryArtworkCard.tsx` and as
 * `buildScoreBoxes()` / an inline `statusText` chain inside `directoryUpload.ts`,
 * left over from a half-finished conversion. `buildScoreBoxes` was byte-identical
 * to `ScoreBoxes`, but the two status implementations had **drifted** — see the
 * note on `StatusDisplay`.
 */

/** The ten-box optimisation-score meter. */
export function ScoreBoxes({ score }: { score: number }) {
  const boxes = [];
  for (let i = 1; i <= 10; i++) {
    boxes.push(
      <span
        key={i}
        class={`view-data-scorebox2${score >= i ? " view-data-scorebox2-filled" : ""}`}
      />
    );
  }
  return <div class="directory-table-scoreboxes">{boxes}</div>;
}

/**
 * The coloured for-sale status dot and its label — deliberately two states only.
 *
 * It used to have five ("Data missing" for no category, "Low resolution",
 * "Expanded Licensing" for a pending opt-in). Artists read every red label as
 * "my artwork is not selling", and "Data missing" in particular was noise: the
 * AI pass fills the metadata in, and a missing category never stopped a sale.
 * So an artwork is simply for sale (green) when it is READY_FOR_SALE at a
 * sellable resolution, and not for sale (red) otherwise — including hidden-sale
 * accounts and artworks too small to sell. The Expanded-Licensing opt-in still
 * has its own button next to the status on the card.
 */
export function StatusDisplay({ stage, megapixels }: { stage: string; megapixels: number | null }) {
  const forSale =
    !config.hiddenSale && stage === "READY_FOR_SALE" && megapixels !== null && megapixels > 8;
  if (forSale) {
    return (
      <>
        <span class="directory-dot-green" /> For sale, {megapixels} MP
      </>
    );
  }
  return (
    <>
      <span class="directory-dot-red" /> Not for sale
    </>
  );
}
